import { useState } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { act } from "@/lib/hooks";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Section, Field } from "@/components/common";
import { CountryAddressFields } from "@/pages/platform/TenantAdminPanel";
import { COUNTRY_NAME } from "@/lib/countries";

export function CompanyProfile({ d, can, onSaved }) {
  const { refresh } = useAuth();
  const [f, setF] = useState({ name: d.name, contact_phone: d.contact_phone || "", country: d.country || "", base_currency: d.base_currency, address: d.address || {}, logo: d.logo || null });
  const [review, setReview] = useState(null);
  const onLogo = (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) { act(() => Promise.reject(new Error("Logo must be under 500 KB"))); return; }
    const r = new FileReader();
    r.onload = () => setF((x) => ({ ...x, logo: r.result }));
    r.readAsDataURL(file);
  };
  const save = async () => {
    const r = await act(() => api.put("/tenant/settings/profile", f), "Company profile saved");
    if (!r) return;
    refresh?.();
    if (r.tax_review_needed) setReview(r.suggested_rules); else onSaved();
  };
  const loadDefaults = async () => {
    if (await act(() => api.put("/tenant/settings/taxes", { rules: review }), `${COUNTRY_NAME[f.country]} tax rules loaded`)) { setReview(null); onSaved(); }
  };
  return (
    <Section title="Company profile" testId="company-profile">
      <div className="grid gap-3 p-5 sm:grid-cols-2">
        <Field label="Company name"><Input data-testid="profile-name-input" disabled={!can} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Phone"><Input data-testid="profile-phone-input" disabled={!can} value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} /></Field>
        <CountryAddressFields f={f} setF={setF} prefix="profile" currencyLocked={d.currency_locked} />
        <div className="space-y-1 sm:col-span-2">
          <span className="text-sm font-medium text-slate-700">Logo</span>
          <div className="flex items-center gap-3">
            {f.logo ? <img src={f.logo} alt="Logo" className="h-12 w-auto rounded border border-slate-200 bg-white p-1" data-testid="profile-logo-preview" /> : <span className="text-xs text-slate-400">No logo</span>}
            {can && <label className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-[#389F95]"><Upload className="h-4 w-4" />Upload (PNG/JPG, max 500 KB)<input type="file" accept="image/*" className="hidden" data-testid="profile-logo-input" onChange={(e) => onLogo(e.target.files[0])} /></label>}
          </div>
        </div>
        {d.currency_locked && <p className="text-xs text-slate-500 sm:col-span-2">Base currency is locked because accounting entries exist.</p>}
      </div>
      {can && <div className="border-t border-slate-200 px-5 py-3"><Button data-testid="profile-save-button" disabled={!f.name || !f.country} onClick={save} className="bg-[#13294B] hover:bg-[#1c3a66]">Save company profile</Button></div>}
      <Dialog open={!!review} onOpenChange={() => { setReview(null); onSaved(); }}>
        <DialogContent data-testid="tax-review-dialog">
          <DialogHeader><DialogTitle>Review your tax rules</DialogTitle>
            <DialogDescription>You changed your country to {COUNTRY_NAME[f.country]}. Your current tax rules were kept unchanged. {review?.length ? `You can replace them with the ${review.length} default rule(s) for ${COUNTRY_NAME[f.country]}, or keep yours and edit them manually.` : `There are no pre-loaded rules for ${COUNTRY_NAME[f.country]}; please review and edit your tax rules manually.`}</DialogDescription></DialogHeader>
          {review?.length > 0 && <ul className="text-sm text-slate-600">{review.map((r) => <li key={r.code}>{r.name} · {r.rate}%</li>)}</ul>}
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="tax-review-keep" onClick={() => { setReview(null); onSaved(); }}>Keep my current rules</Button>
            {review?.length > 0 && <Button data-testid="tax-review-load" onClick={loadDefaults} className="bg-[#13294B]">Replace with {COUNTRY_NAME[f.country]} defaults</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
